import { FC, useEffect, useMemo } from 'react';

import styled from '@emotion/styled';
import { useFormik } from 'formik';
import { CandidatePersonalDetails, CandidatePersonalDetailsFormModel } from 'store/candidate/types';
import { ServerErrors } from 'components';
import { mapServerError } from 'core';
import { useExportedRegistrationsCheckQuery } from 'store/candidate/candidateAPI';
import { useGetAllCountriesQuery } from 'store/country/countryAPI';
import { useGetFamilyRelationshipsQuery } from 'store/family-relationship/familyRelationshipAPI';
import { useSaveCandidateDetailsMutation } from 'store/candidate/candidateAPI';

import { useFormFields, useServerErrors } from '@britishcouncil/react-forms';
import { Alert, Button, LoadingContainer } from '@britishcouncil/react-solas';

import { VALIDATION_SCHEMA } from '../validationSchema';

import { CandidateFormFields } from './CandidateFormFields';
import { DEFAULT_FORM_VALUES } from './candidateFormDefaultValues';

export const CandidateForm: FC<{
  candidate: CandidatePersonalDetails;
  onCancel: () => void;
  onSuccess: (emailOrNameChanged: boolean) => void;
}> = ({ candidate, onCancel, onSuccess }) => {
  const [
    saveCandidateDetails,
    { isSuccess: submitted, isLoading: submitting, error: submitErrors, isError: isSubmitError },
  ] = useSaveCandidateDetailsMutation();
  const { data: countriesOptions, isFetching: countriesLoading } = useGetAllCountriesQuery();
  const { data: familyRelationshipOptions, isFetching: relationshipsLoading } = useGetFamilyRelationshipsQuery();
  const { data: registrationCheck, isFetching: registrationChecksLoading } = useExportedRegistrationsCheckQuery(
    candidate.id
  );

  const serverErrors = useMemo(
    () => (isSubmitError ? mapServerError(submitErrors) : undefined),
    [submitErrors, isSubmitError]
  );

  const { canEditUciEdexcel, canEditUciAqa } = useMemo(
    () => ({
      canEditUciEdexcel:
        registrationCheck?.isCurrentEdexcelSession &&
        !registrationCheck?.hasExportedNotCancelledExamsInCurrentEdexcelSession,
      canEditUciAqa:
        registrationCheck?.isCurrentAqaSession && !registrationCheck?.hasExportedNotCancelledExamsInCurrentAqaSession,
    }),
    [registrationCheck]
  );

  //TODO[GP - future fix]: workaround because API return name instead of id...
  const defaultRelationshipId = useMemo(
    () =>
      familyRelationshipOptions
        ?.find((x) => x.name === candidate.guardianContactDetails?.familyRelationship)
        ?.id.toString() ?? '',
    [familyRelationshipOptions, candidate]
  );

  const formik = useFormik<CandidatePersonalDetailsFormModel>({
    enableReinitialize: true,
    validationSchema: VALIDATION_SCHEMA,
    initialValues: {
      ...DEFAULT_FORM_VALUES,
      ...candidate,
      uciAqa: candidate.uciAqa ?? '',
      uciEdexcel: candidate.uciEdexcel ?? '',
      guardianContactDetails: candidate.guardianContactDetails
        ? { ...candidate.guardianContactDetails, familyRelationshipId: defaultRelationshipId }
        : DEFAULT_FORM_VALUES.guardianContactDetails,
    },
    onSubmit: (values) => {
      saveCandidateDetails({
        ...candidate,
        ...values,
        uciAqa: values.uciAqa ? values.uciAqa : null,
        uciEdexcel: values.uciEdexcel ? values.uciEdexcel : null,
        guardianContactDetails: candidate.isChild ? values.guardianContactDetails : null,
      });
    },
  });

  const fields = useFormFields(formik);
  const mainErrors = useServerErrors(formik, serverErrors);

  const emailOrNameChanged =
    !candidate.isChild &&
    (formik.values.email.trim().toUpperCase() !== candidate.email.trim().toUpperCase() ||
      formik.values.otherNames.trim().toUpperCase() !== candidate.otherNames.trim().toUpperCase() ||
      formik.values.hasFamilyName !== candidate.hasFamilyName ||
      (formik.values.hasFamilyName &&
        formik.values.familyName!.trim().toUpperCase() !== candidate.familyName!.trim().toUpperCase()));

  useEffect(() => {
    if (submitted) {
      onSuccess(emailOrNameChanged);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitted, emailOrNameChanged]);

  return (
    <LoadingContainer loading={countriesLoading || relationshipsLoading || registrationChecksLoading}>
      <form onSubmit={formik.handleSubmit}>
        {emailOrNameChanged && (
          <Alert type="warning">
            <strong>Changing last or first name will force logging out!</strong>
          </Alert>
        )}

        <CandidateFormFields
          candidate={candidate}
          fields={fields}
          countriesOptions={countriesOptions || []}
          familyRelationshipOptions={familyRelationshipOptions || []}
          canEditUciAqa={canEditUciAqa}
          canEditUciEdexcel={canEditUciEdexcel}
        />

        {!!mainErrors && !submitting && <ServerErrors errors={mainErrors} />}

        <ButtonsContainer>
          <Button intent="default" type="button" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button intent="primary" type='submit' disabled={submitting} loading={submitting}>
            Save changes
          </Button>
        </ButtonsContainer>
      </form>
    </LoadingContainer>
  );
};

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;
